/* eslint @typescript-eslint/no-explicit-any: 0 */
import {Dispatch, useState, useCallback} from 'react';
import {
    Box,
    Grid,
    TextField,
    MenuItem,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    Switch,
    CircularProgress,
} from '@material-ui/core';
import {LockOutlined} from '@material-ui/icons';
import {FrequencyOptions, DateOptions} from './SelectOptions';
import {AccountType, PaymentInformation} from '../types';
import {useBankVerification} from '@/Agent/Common/useBankVerification';
import {BankVerificationError} from '@/Agent/Common/BankVerificationError';
import RootStore from '@/Core/mobx/RootStore';

interface BankingInfoProps {
    index: number;
    value: PaymentInformation;
    updateFunction: Dispatch<any>;
    customerEmail?: string; // Added optional customerEmail prop
    customerGuid?: string; // Added optional customerGuid prop
}

function BankingInformationForm(props: BankingInfoProps) {
    const {index, value, updateFunction, customerEmail, customerGuid} = props;
    const parameterStore = RootStore.agentPortalStore.parameterStore;

    const [bankingInfo, setBankingInfo] = useState(
        () =>
            value || {
                id: '',
                ownerName: '',
                institutionName: '',
                paymentFrequency: '',
                dayOfMonth: '',
                routingNumber: '',
                accountNumber: '',
                accountType: AccountType.CHECKING,
            },
    );

    const [loading, setLoading] = useState(false);
    const {
        verificationResult,
        overrideConfirmed,
        setOverrideConfirmed,
        isVerifying,
        verifyBankAccount,
        resetVerification,
        canProceed,
    } = useBankVerification();

    const handleBankingInfoChange = (
        event: any,
        field:
            | 'id'
            | 'label'
            | 'routingNumber'
            | 'accountNumber'
            | 'paymentFrequency'
            | 'dayOfMonth'
            | 'plaidAccountId'
            | 'updatedAt'
            | 'ownerName'
            | 'institutionName'
            | 'accountType',
    ) => {
        value[field] = event.target.value;
        setBankingInfo({...value});
        // Reset verification when bank info changes
        if (field === 'routingNumber' || field === 'accountNumber' || field === 'accountType') {
            resetVerification();
        }
    };

    const handleVerifyBankAccount = useCallback(async () => {
        if (!bankingInfo.routingNumber || !bankingInfo.accountNumber) {
            return {success: false};
        }

        const accountType = bankingInfo.accountType === AccountType.CHECKING ? 1 : 2;

        // Log for debugging
        console.log('Verifying bank account with:', {
            customerGuid,
            customerEmail: customerEmail || parameterStore.contact,
            formId: parameterStore.formid, // Note: lowercase 'formid' in ParameterStore
            walkthroughDataId: parameterStore.walkthroughData?.uuid || parameterStore.walkthroughData?.id,
        });

        const result = await verifyBankAccount({
            routingNumber: bankingInfo.routingNumber,
            accountNumber: bankingInfo.accountNumber,
            accountType,
            customerEmail: customerEmail || parameterStore.contact || '',
            customerGuid: customerGuid || parameterStore.walkthroughData?.uuid || parameterStore.walkthroughData?.id,
            formId: parameterStore.formid, // Note: lowercase 'formid' in ParameterStore
            // ageniumCaseId not available in ParameterStore
        });
        return result;
    }, [
        bankingInfo.routingNumber,
        bankingInfo.accountNumber,
        bankingInfo.accountType,
        customerEmail,
        customerGuid,
        parameterStore.contact,
        parameterStore.formid,
        parameterStore.walkthroughData,
        verifyBankAccount,
    ]);

    const canSave = useCallback(() => {
        // Don't allow save if bank info is incomplete
        if (!bankingInfo.routingNumber || !bankingInfo.accountNumber) {
            return false;
        }
        // If not verified yet, allow clicking (will trigger verification)
        if (!verificationResult) return true;
        // Otherwise check if can proceed based on verification result
        return canProceed();
    }, [bankingInfo.routingNumber, bankingInfo.accountNumber, verificationResult, canProceed]);

    const handleSaveClick = useCallback(async () => {
        try {
            // Always verify before saving
            const verifyResult = await handleVerifyBankAccount();

            if (!verifyResult) {
                console.error('❌ Verification returned null/undefined');
                return;
            }

            if (!verifyResult.success) {
                console.log('❌ Verification failed:', verifyResult);
                // Show the error but don't save
                return;
            }

            // Can proceed with save
            setLoading(true);
            updateFunction({childVal: {index, ...bankingInfo}, setLoading});
        } catch (error) {
            setLoading(false);
        }
    }, [bankingInfo, verificationResult, handleVerifyBankAccount, updateFunction, index]);

    return (
        <Box>
            <Grid container spacing={3} xs={12}>
                <Grid item xs={12}>
                    <Grid container spacing={1} direction="row" alignItems="center">
                        <Grid item>
                            <LockOutlined fontSize="small" />
                        </Grid>
                        <Grid item>
                            <h4>Bank Name</h4>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={8} direction="row" alignItems="center">
                        <Grid item xs={3}>
                            <TextField
                                id={`account_holder_${index}`}
                                label="Account Holder Name"
                                placeholder="--"
                                variant="standard"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={bankingInfo.ownerName}
                                onChange={(e) => {
                                    handleBankingInfoChange(e, 'ownerName');
                                }}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                id={`bank_name_${index}`}
                                label="Bank Name"
                                placeholder="--"
                                variant="standard"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={bankingInfo.institutionName}
                                onChange={(e) => {
                                    handleBankingInfoChange(e, 'institutionName');
                                }}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                id={`payment_frequency_${index}`}
                                select
                                fullWidth
                                label="Payment Frequency"
                                placeholder="--"
                                variant="standard"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={bankingInfo.paymentFrequency}
                                onChange={(e) => {
                                    handleBankingInfoChange(e, 'paymentFrequency');
                                }}
                            >
                                {FrequencyOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                id={`draft_date_${index}`}
                                select
                                fullWidth
                                label="Draft Date"
                                placeholder="--"
                                variant="standard"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={bankingInfo.dayOfMonth}
                                onChange={(e) => {
                                    handleBankingInfoChange(e, 'dayOfMonth');
                                }}
                            >
                                {DateOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label} of the month
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={8} direction="row" alignItems="center">
                        <Grid item xs={3}>
                            <TextField
                                id={`routing_number_${index}`}
                                label="Routing Number"
                                placeholder="--"
                                variant="standard"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={bankingInfo.routingNumber}
                                onChange={(e) => {
                                    handleBankingInfoChange(e, 'routingNumber');
                                }}
                                onBlur={handleVerifyBankAccount}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                id={`account_number_${index}`}
                                label="Account Number"
                                placeholder="--"
                                variant="standard"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={bankingInfo.accountNumber}
                                onChange={(e) => {
                                    handleBankingInfoChange(e, 'accountNumber');
                                }}
                                onBlur={handleVerifyBankAccount}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl>
                                <FormLabel id={`account_type_${index}`}>Account Type</FormLabel>
                                <RadioGroup
                                    row
                                    aria-labelledby={`account_type_${index}`}
                                    defaultValue="checking"
                                    name={`account_type_radio_button_groups_${index}`}
                                    value={bankingInfo.accountType}
                                    onChange={(e) => {
                                        handleBankingInfoChange(e, 'accountType');
                                    }}
                                >
                                    <FormControlLabel
                                        value="checking"
                                        control={<Radio size="small" color="secondary" />}
                                        label={
                                            <Box component="div" fontSize={12}>
                                                checking
                                            </Box>
                                        }
                                    />
                                    <FormControlLabel
                                        value="savings"
                                        control={<Radio size="small" color="secondary" />}
                                        label={
                                            <Box component="div" fontSize={12}>
                                                savings
                                            </Box>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl>
                                <FormLabel id={`conditional_coverage_${index}`}>
                                    Was conditional coverage elected?
                                </FormLabel>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <BankVerificationError
                        verificationResult={verificationResult}
                        overrideConfirmed={overrideConfirmed}
                        onOverrideChange={setOverrideConfirmed}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        id={`save_req_${index}`}
                        variant="outlined"
                        color="primary"
                        onClick={handleSaveClick}
                        disabled={!canSave() || isVerifying}
                    >
                        {loading || isVerifying ? (
                            <CircularProgress size={30} />
                        ) : (
                            <>Save {index > -1 ? 'Requirement' : 'Bank Information'}</>
                        )}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
}

export default BankingInformationForm;
